import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/apiClient";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Building2, Save, Loader2, Bot, ShieldCheck, RotateCcw } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { MrvCombosCard } from "@/components/MrvCombosCard";

const FIELDS = [
  ["denominazione", "Denominazione", "sm:col-span-2"],
  ["sottotitolo", "Sottotitolo / Agenzia", "sm:col-span-2"],
  ["rui_sezione", "Sezione RUI", ""],
  ["rui_numero", "Numero RUI", ""],
  ["sede_legale", "Sede legale", "sm:col-span-2"],
  ["telefono", "Telefono", ""],
  ["cell", "Cellulare", ""],
  ["email", "Email", ""],
  ["pec", "PEC", ""],
  ["iban", "IBAN (incasso premi)", "sm:col-span-2"],
  ["sito", "Sito web", ""],
  ["impresa_preponente", "Impresa preponente", ""],
];

export default function Settings() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [relModel, setRelModel] = useState(null);
  const [savingModel, setSavingModel] = useState(false);
  const [compNotes, setCompNotes] = useState(null);
  const [compDefaults, setCompDefaults] = useState([]);
  const [compAdesione, setCompAdesione] = useState("");
  const [compAdesioneDefault, setCompAdesioneDefault] = useState("");
  const [compEmail, setCompEmail] = useState("");
  const [savingComp, setSavingComp] = useState(false);

  useEffect(() => {
    api.get("/admin/settings/assietica").then(({ data }) => setForm(data));
    api.get("/admin/settings/relazione-model").then(({ data }) => setRelModel(data.model)).catch(() => setRelModel("gpt-5.4"));
    api.get("/admin/settings/compliance").then(({ data }) => {
      setCompNotes(data.notes); setCompDefaults(data.defaults || []);
      setCompAdesione(data.adesione_notice || ""); setCompAdesioneDefault(data.adesione_default || "");
      setCompEmail(data.compliance_email || "");
    }).catch(() => {});
  }, []);

  const updateNote = (i, text) => setCompNotes((prev) => prev.map((n, idx) => (idx === i ? { ...n, text } : n)));
  const saveCompliance = async () => {
    setSavingComp(true);
    try {
      const { data } = await api.put("/admin/settings/compliance", { notes: compNotes, adesione_notice: compAdesione, compliance_email: compEmail });
      setCompNotes(data.notes); setCompAdesione(data.adesione_notice || ""); setCompEmail(data.compliance_email || "");
      toast.success("Note di compliance aggiornate. Ricarica le pagine per vederle ovunque.");
    } catch (e) { toast.error(apiError(e)); }
    finally { setSavingComp(false); }
  };
  const resetCompliance = () => { setCompNotes(compDefaults.map((n) => ({ ...n }))); setCompAdesione(compAdesioneDefault); };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings/assietica", form);
      toast.success("Impostazioni salvate. Il MUP userà i nuovi dati.");
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const saveModel = async (m) => {
    setRelModel(m);
    setSavingModel(true);
    try {
      await api.put("/admin/settings/relazione-model", { model: m });
      toast.success("Modello predefinito delle relazioni aggiornato.");
    } catch (e) { toast.error(apiError(e)); }
    finally { setSavingModel(false); }
  };

  if (!form) return null;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-3xl font-semibold text-forest">Impostazioni intermediario principale</h1>
        <p className="mt-1 text-muted-foreground">Dati fissi di Assietica utilizzati nel MUP (Allegato 3 IVASS) e nei documenti.</p>
      </div>

      <Card className="max-w-3xl rounded-xl border-border/60 p-6">
        <div className="mb-5 flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-sage text-forest"><Building2 className="h-6 w-6" /></div>
          <div>
            <h2 className="font-heading text-lg font-semibold text-forest">{form.denominazione}</h2>
            <p className="text-xs text-muted-foreground">Intermediario principale · RUI Sez. {form.rui_sezione} n. {form.rui_numero}</p>
          </div>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {FIELDS.map(([key, label, span]) => (
            <div key={key} className={`space-y-1.5 ${span}`}>
              <Label>{label}</Label>
              <Input data-testid={`settings-${key}`} value={form[key] || ""} onChange={(e) => setForm({ ...form, [key]: e.target.value })} />
            </div>
          ))}
        </div>
        <div className="mt-6 flex justify-end">
          <Button onClick={save} disabled={saving} data-testid="settings-save" className="gap-2 rounded-lg bg-terracotta hover:bg-terracotta-hover">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Salva impostazioni
          </Button>
        </div>
      </Card>

      <Card className="max-w-3xl rounded-xl border-border/60 p-6" data-testid="settings-relazione-model-card">
        <div className="mb-4 flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-sage text-forest"><Bot className="h-6 w-6" /></div>
          <div>
            <h2 className="font-heading text-lg font-semibold text-forest">Modello AI predefinito — Relazioni Tecniche</h2>
            <p className="text-xs text-muted-foreground">Modello usato per le nuove relazioni di tutti i prodotti. I partner possono comunque cambiarlo caso per caso nel fascicolo.</p>
          </div>
        </div>
        <div className="max-w-sm space-y-1.5">
          <Label>Modello predefinito</Label>
          <Select value={relModel || "gpt-5.4"} onValueChange={saveModel} disabled={savingModel}>
            <SelectTrigger data-testid="settings-relazione-model" className="h-11"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="gpt-5.4">GPT-5.4 — sintetico e conservativo</SelectItem>
              <SelectItem value="claude-sonnet-4-6">Claude Sonnet 4.6 — dettagliato e più citato</SelectItem>
            </SelectContent>
          </Select>
          {savingModel && <p className="flex items-center gap-1.5 text-xs text-muted-foreground"><Loader2 className="h-3 w-3 animate-spin" /> Salvataggio…</p>}
        </div>
      </Card>

      <Card className="max-w-3xl rounded-xl border-border/60 p-6" data-testid="settings-compliance-card">
        <div className="mb-4 flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-sage text-forest"><ShieldCheck className="h-6 w-6" /></div>
          <div>
            <h2 className="font-heading text-lg font-semibold text-forest">Note di compliance</h2>
            <p className="text-xs text-muted-foreground">Fonte unica: questi testi appaiono su tutte le landing, nel portale partner e nel gestionale. Modificabili dall'ufficio compliance.</p>
          </div>
        </div>
        {compNotes === null ? (
          <p className="flex items-center gap-1.5 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Caricamento…</p>
        ) : (
          <div className="space-y-4">
            {compNotes.map((n, i) => (
              <div key={n.key || i} className="space-y-1.5">
                <Label>{n.label}</Label>
                <Textarea data-testid={`compliance-note-${n.key || i}`} value={n.text} onChange={(e) => updateNote(i, e.target.value)} rows={2} />
              </div>
            ))}
            <div className="space-y-1.5 border-t border-border/50 pt-4">
              <Label>Nota legale — Pagina «Adesione a distanza» (IVASS)</Label>
              <p className="text-[11px] text-muted-foreground">Testo dedicato alla pagina di sottoscrizione a distanza (diverso dalle note informative sopra).</p>
              <Textarea data-testid="compliance-adesione" value={compAdesione} onChange={(e) => setCompAdesione(e.target.value)} rows={5} />
            </div>
            <div className="space-y-1.5 border-t border-border/50 pt-4">
              <Label>Casella compliance (copia dei moduli firmati)</Label>
              <p className="text-[11px] text-muted-foreground">Ad ogni invio del «Modulo di Adesione firmato» al cliente, una copia con PDF allegato viene inviata a questo indirizzo per la tracciabilità d'ufficio.</p>
              <Input data-testid="compliance-email" type="email" value={compEmail} onChange={(e) => setCompEmail(e.target.value)} placeholder="compliance@assietica.it" />
            </div>
            <div className="flex flex-wrap gap-2 pt-1">
              <Button onClick={saveCompliance} disabled={savingComp} data-testid="compliance-save" className="gap-2 rounded-lg bg-terracotta hover:bg-terracotta-hover">
                {savingComp ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Salva note
              </Button>
              <Button variant="outline" onClick={resetCompliance} disabled={savingComp || !compDefaults.length} data-testid="compliance-reset" className="gap-2 rounded-lg">
                <RotateCcw className="h-4 w-4" /> Ripristina predefiniti
              </Button>
            </div>
          </div>
        )}
      </Card>

      <MrvCombosCard />
    </div>
  );
}
